import React, { FC } from "react"
import ContentTypes, { EntryWithoutUnresolvableLinks } from "../../../utils/ContentTypes"
import { Modal } from "./content-types/modal"
import { ContentfulConditionalGate } from "../../../lib/contentful/components/ContentfulConditionGate"
import { defaultMarkdownElements } from "@internal/ui"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  DownIcon,
  UnorderedList
} from "@carrotfertility/carotene-core"
import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import type { Node } from "@contentful/rich-text-types"
import { ContentfulContentProp } from "../legacy/content-types/shared-types"
import {
  TypeBlockQuoteSkeleton,
  TypeContainerSkeleton,
  TypeContentBlockSkeleton,
  TypeContentBlockWithIconSkeleton,
  TypeTwoColumnBulletListSkeleton,
  TypeTwoColumnLayoutSkeleton,
  TypeModalSkeleton,
  TypeUnorderedListSkeleton,
  TypeAccordionSkeleton,
  TypeBannerImageSkeleton
} from "#/types/contentful"
import { ContentfulMarkdown } from "#/services/contentful/contentful-markdown-renderer"
import { RichTextContext } from "#/services/contentful/shared/rich-text"
import { ContentfulContentEntry } from "#/services/contentful/contentful-content-entry"

const contentBlockRichTextOptions = (embeddedEntry: Node) => {
  const entry = embeddedEntry.data.target
  if (entry && entry.sys && entry.fields) {
    return (
      <Box component="span">
        <RichTextContext.Provider value={{ isInRichText: true }}>
          <Content content={entry as ContentfulContentEntry} />
        </RichTextContext.Provider>
      </Box>
    )
  }
}

function isAccordian(entry: ContentfulContentEntry): entry is EntryWithoutUnresolvableLinks<TypeAccordionSkeleton> {
  return entry.sys.contentType.sys.id === ContentTypes.ACCORDION
}

function isBlockQuote(entry: ContentfulContentEntry): entry is EntryWithoutUnresolvableLinks<TypeBlockQuoteSkeleton> {
  return entry.sys.contentType.sys.id === ContentTypes.BLOCK_QUOTE
}

function isContainer(entry: ContentfulContentEntry): entry is EntryWithoutUnresolvableLinks<TypeContainerSkeleton> {
  return entry.sys.contentType.sys.id === ContentTypes.CONTAINER
}

function isContentBlock(
  entry: ContentfulContentEntry
): entry is EntryWithoutUnresolvableLinks<TypeContentBlockSkeleton> {
  return entry.sys.contentType.sys.id === ContentTypes.CONTENT_BLOCK
}

function isContentBlockWithIcon(
  entry: ContentfulContentEntry
): entry is EntryWithoutUnresolvableLinks<TypeContentBlockWithIconSkeleton> {
  return entry.sys.contentType.sys.id === ContentTypes.CONTENT_BLOCK_WITH_ICON
}

function isTwoColumnBulletList(
  entry: ContentfulContentEntry
): entry is EntryWithoutUnresolvableLinks<TypeTwoColumnBulletListSkeleton> {
  return entry.sys.contentType.sys.id === ContentTypes.TWO_COLUMN_BULLET_LIST
}

function isTwoColumnLayout(
  entry: ContentfulContentEntry
): entry is EntryWithoutUnresolvableLinks<TypeTwoColumnLayoutSkeleton> {
  return entry.sys.contentType.sys.id === ContentTypes.TWO_COLUMN_LAYOUT
}

function isUnorderedList(
  entry: ContentfulContentEntry
): entry is EntryWithoutUnresolvableLinks<TypeUnorderedListSkeleton> {
  return entry.sys.contentType.sys.id === ContentTypes.UNORDERED_LIST
}

function isModal(entry: ContentfulContentEntry): entry is EntryWithoutUnresolvableLinks<TypeModalSkeleton> {
  return entry.sys.contentType.sys.id === ContentTypes.MODAL
}

function isBannerImage(entry: ContentfulContentEntry): entry is EntryWithoutUnresolvableLinks<TypeBannerImageSkeleton> {
  return entry.sys.contentType.sys.id === ContentTypes.BANNER_IMAGE
}

export type RecursiveContentfulContentRendererProps = {
  contents: ContentfulContentEntry[]
  asList?: boolean | false
}

export const RecursiveContentfulContentRenderer: FC<RecursiveContentfulContentRendererProps> = ({
  contents,
  asList = false
}) => {
  return (
    <>
      {contents?.map(
        (content) =>
          content.fields && (
            <ContentfulConditionalGate conditions={content.fields.conditions} key={content.sys.id}>
              {asList ? (
                <defaultMarkdownElements.li.component {...defaultMarkdownElements.li.props}>
                  <Content content={content} />
                </defaultMarkdownElements.li.component>
              ) : (
                <Content content={content} />
              )}
            </ContentfulConditionalGate>
          )
      )}
    </>
  )
}

const Content: FC<ContentfulContentProp<ContentfulContentEntry>> = ({ content }) => {
  if (isContentBlockWithIcon(content)) {
    return <ContentBlockWithIcon content={content} />
  } else if (isTwoColumnLayout(content)) {
    return (
      <Box
        display="flex"
        gap={(theme) => theme.spacing(theme.tokens.spacing.md)}
        flexWrap="wrap"
        sx={(theme) => ({
          "& > *": { inlineSize: { xs: "100%", md: `calc(50% - ${theme.spacing(theme.tokens.spacing.md)})` } }
        })}
      >
        <RecursiveContentfulContentRenderer contents={content.fields?.content} />
      </Box>
    )
  } else if (isTwoColumnBulletList(content)) {
    return (
      <UnorderedList
        component="ul"
        sx={(theme) => ({
          display: "flex",
          gap: theme.spacing(theme.tokens.spacing.xs),
          paddingBlockStart: 0,
          paddingBlockEnd: (theme) => theme.spacing(theme.tokens.spacing.xl),
          flexWrap: "wrap",
          li: {
            "&:nth-child(odd)": {
              paddingInlineEnd: (theme) => theme.spacing(theme.tokens.spacing.sm)
            },
            inlineSize: { xs: "100%", md: `calc(50% - ${theme.spacing(theme.tokens.spacing.md)})` }
          }
        })}
      >
        <RecursiveContentfulContentRenderer contents={content.fields?.content} asList />
      </UnorderedList>
    )
  } else if (isBlockQuote(content)) {
    const backgroundColorToSolidColor: Record<string, "info" | "success"> = {
      info: "info",
      success: "success"
    }
    const backgroundColor = content.fields?.backgroundColor
    const solidColor = backgroundColor ? backgroundColorToSolidColor[backgroundColor] : undefined
    const tileProps = solidColor ? { color: solidColor } : {}

    return (
      <defaultMarkdownElements.blockquote.component {...defaultMarkdownElements.blockquote.props} {...tileProps}>
        <RecursiveContentfulContentRenderer contents={content.fields?.content} />
      </defaultMarkdownElements.blockquote.component>
    )
  } else if (isContainer(content)) {
    return <RecursiveContentfulContentRenderer contents={content.fields?.content} />
  } else if (isModal(content)) {
    return <Modal content={content} />
  } else if (isContentBlock(content)) {
    return (
      <>
        {content.fields?.body && <ContentfulMarkdown>{content.fields.body}</ContentfulMarkdown>}
        {content.fields?.richTextBody && (
          <Box>
            {documentToReactComponents(content.fields.richTextBody, {
              renderNode: {
                [INLINES.EMBEDDED_ENTRY]: contentBlockRichTextOptions,
                [BLOCKS.EMBEDDED_ENTRY]: contentBlockRichTextOptions
              }
            })}
          </Box>
        )}
      </>
    )
  } else if (isUnorderedList(content)) {
    return (
      <defaultMarkdownElements.ul.component {...defaultMarkdownElements.ul.props}>
        <RecursiveContentfulContentRenderer contents={content.fields?.content} asList />
      </defaultMarkdownElements.ul.component>
    )
  } else if (isBannerImage(content)) {
    return (
      <Box
        component="img"
        src={content.fields?.image.fields.file?.url}
        alt={content.fields?.altText}
        sx={(theme) => ({
          borderRadius: theme.tokens.borderRadius.md,
          marginBlockStart: theme.tokens.spacing.md,
          marginBlockEnd: theme.tokens.spacing.lg
        })}
      />
    )
  } else if (isAccordian(content)) {
    return <ContentAccordion content={content} />
  }

  return null
}

function ContentAccordion({ content }: { content: EntryWithoutUnresolvableLinks<TypeAccordionSkeleton> }) {
  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<DownIcon fontSize="large" />}
        aria-controls={`content-accordion-details-${content.sys.id}`}
        id={`content-accordion-${content.sys.id}`}
      >
        <ContentfulMarkdown>{content.fields?.summaryClickableElement}</ContentfulMarkdown>
      </AccordionSummary>
      <AccordionDetails>
        <RecursiveContentfulContentRenderer contents={content.fields?.content} />
      </AccordionDetails>
    </Accordion>
  )
}

const iconDisplaySizeMap: Record<"large" | "medium" | "small", number> = {
  small: 24,
  medium: 60,
  large: 120
}

function ContentBlockWithIcon({
  content
}: {
  content: EntryWithoutUnresolvableLinks<TypeContentBlockWithIconSkeleton>
}) {
  const alignment = content.fields.iconAlignment ?? "start"
  const { iconDisplaySize, customSize } = content.fields
  const iconSize =
    iconDisplaySize === "custom"
      ? customSize ?? iconDisplaySizeMap.medium
      : iconDisplaySizeMap[iconDisplaySize] ?? iconDisplaySizeMap.medium

  const icon = content.fields.icon?.fields && (
    <Box component="img" src={content.fields.icon.fields.file?.url} height={iconSize} width={iconSize} alt="" />
  )
  const body = content.fields?.body && <ContentfulMarkdown>{content.fields.body}</ContentfulMarkdown>

  return (
    <Box
      display="flex"
      gap={(theme) => theme.spacing(theme.tokens.spacing.md)}
      paddingTop={(theme) => theme.spacing(theme.tokens.spacing.xs)}
      paddingBottom={(theme) => theme.spacing(theme.tokens.spacing.lg)}
      alignItems="center"
      flexDirection={alignment === "start" ? "row" : "row-reverse"}
    >
      {icon}
      <Box flex={1}>{body}</Box>
    </Box>
  )
}
